import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, Search, CheckCircle2, XCircle } from "lucide-react";
import {
  listReportAccessMatrix,
  setReportAccess,
  bulkSetReportAccess,
} from "@/lib/portal-reports/admin.functions";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute("/_authenticated/app/admin/portal-reports/access")({
  component: PortalReportsAccess,
});

function PortalReportsAccess() {
  const qc = useQueryClient();
  const matrixFn = useServerFn(listReportAccessMatrix);
  const setFn = useServerFn(setReportAccess);
  const bulkFn = useServerFn(bulkSetReportAccess);

  const { data, isLoading } = useQuery({
    queryKey: ["portal-reports-access-matrix"],
    queryFn: () => runServerFn(() => matrixFn(), "Loading access matrix"),
  });

  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const customers = data?.customers ?? [];
  const reports = data?.reports ?? [];

  const filteredCustomers = useMemo(() => {
    if (!search) return customers;
    const s = search.toLowerCase();
    return customers.filter((c) => c.name.toLowerCase().includes(s));
  }, [customers, search]);

  async function toggle(customerId: string, reportKey: string, next: boolean) {
    const k = `${customerId}::${reportKey}`;
    setPending((p) => ({ ...p, [k]: true }));
    try {
      await runServerFn(
        () => setFn({ data: { customerId, reportKey, enabled: next } }),
        "Updating access",
      );
      qc.setQueryData(["portal-reports-access-matrix"], (prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          customers: prev.customers.map((c: any) =>
            c.id === customerId
              ? { ...c, access: { ...c.access, [reportKey]: next } }
              : c,
          ),
        };
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to update access");
    } finally {
      setPending((p) => {
        const { [k]: _, ...rest } = p;
        return rest;
      });
    }
  }

  async function bulk(reportKey: string, enabled: boolean) {
    try {
      await runServerFn(
        () => bulkFn({ data: { reportKey, enabled } }),
        enabled ? "Enabling for all customers" : "Disabling for all customers",
      );
      qc.invalidateQueries({ queryKey: ["portal-reports-access-matrix"] });
      toast.success(`Report ${enabled ? "enabled" : "disabled"} for all customers`);
    } catch (e: any) {
      toast.error(e?.message ?? "Bulk update failed");
    }
  }

  const enabledCount = (c: { access: Record<string, boolean> }) =>
    reports.filter((r) => c.access[r.key] !== false).length;

  return (
    <>
      <PageHeader
        title="Portal Reports — Access Control"
        description="Choose which reports each customer can see in their portal. Reports default to enabled."
        actions={
          <Link to="/app/admin/portal-reports">
            <Button variant="outline" size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to operations
            </Button>
          </Link>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers…"
              className="h-8 pl-7 w-64 text-xs"
            />
          </div>
          <div className="text-xs text-muted-foreground tabular-nums">
            {filteredCustomers.length} customers · {reports.length} reports
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center border border-dashed rounded-md">
            No customers match "{search}".
          </div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="sticky left-0 z-10 bg-muted/40 text-left font-medium px-3 py-2 min-w-[200px]">
                      Customer
                    </th>
                    {reports.map((r) => (
                      <th key={r.key} className="px-2 py-2 text-center font-medium min-w-[110px]">
                        <div className="flex flex-col items-center gap-1">
                          <span className="truncate max-w-[120px]" title={r.title}>{r.title}</span>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => bulk(r.key, true)}
                              className="text-[10px] text-muted-foreground hover:text-foreground"
                              title="Enable for all"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => bulk(r.key, false)}
                              className="text-[10px] text-muted-foreground hover:text-destructive"
                              title="Disable for all"
                            >
                              <XCircle className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-muted/20">
                      <td className="sticky left-0 bg-background z-10 px-3 py-2 font-medium">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{c.name}</span>
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {enabledCount(c)}/{reports.length}
                          </Badge>
                        </div>
                      </td>
                      {reports.map((r) => {
                        const enabled = c.access[r.key] !== false;
                        const k = `${c.id}::${r.key}`;
                        return (
                          <td key={r.key} className="px-2 py-2 text-center">
                            <Switch
                              checked={enabled}
                              disabled={!!pending[k]}
                              onCheckedChange={(v) => toggle(c.id, r.key, v)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}